<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ExchangeRateSourceSelector',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'input']);

  const props = defineProps({
    value: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const internalValue = ref(null);
  const options = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    internalValue: internalValue.value,
    options: options.value,
    ux: ux.value,
  }));

  const $$load_internalValue = async () => {
    return props.value;
  };
  const $$load_options = async () => {
    return $app.value.repo.get('ui_metadata', {
      path: 'exchange_rate_source_options',
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'internalValue' }, { name: 'options' }, { name: 'ux' }],
    loaders: { internalValue: $$load_internalValue, options: $$load_options },
    ux: {},
    dataRefs: { internalValue, options, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('value');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.required,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('required');
    }
  );

  const act_5962862420439144_emit_270dbk47i_getActionArgs = async ($event) => {
    return {
      event: 'input',
      value: internalValue.value,
    };
  };

  const act_5962862420439144_emit_270dbk47i = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_270dbk47i_getActionArgs,
  });
  const inst_evh_5962862420439144_input_mo1xyffnn =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_270dbk47i,
      event: 'input',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_input_mo1xyffnn],
    event: 'input',
  });

  const actions = {
    inst_evh_5962862420439144_input_mo1xyffnn,
    inst_evh_5962862420439144_input,
  };

  const evh_5962862420439144_input_mo1xyffnn = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_input_mo1xyffnn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_input = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div class="input-group" v-if="loaded">
    <div class="input-group-text">
      <ItpIcon icon="ic:baseline-cable"> </ItpIcon>
    </div>
    <ItpFormSelect2
      name="exchange_rate_source"
      v-model="internalValue"
      :value="internalValue"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
          (internalValue =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event);
        evh_5962862420439144_input($event, {});
      "
      :options="options"
      valueField="value"
      textField="name"
      :required="required"
      :readonly="readonly"
    >
    </ItpFormSelect2>
  </div>
</template>
